import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ItemGroup } from '@/components/ui/item';
import {
  toAttachmentSummary,
  useReceiveAttachmentDownload,
  type ReceiveConfirmArrivalResultDto
} from '@/entities/receive';
import { AttachmentItem } from '@/features/consignment-attachment-list';
import { formatDisplayDate } from '@/shared/lib';

import { ConfirmArrivalReceipts } from './confirm-arrival-receipts';
import { ConfirmArrivalWarnings } from './confirm-arrival-warnings';

interface ConfirmArrivalResultProps {
  result: ReceiveConfirmArrivalResultDto;
}

/**
 * What the receipt actually wrote. The GRN numbers matter most — the backend
 * only echoes `printGoodsReceivedNote` back (there is no print endpoint yet), so
 * showing the numbers is how a user who ticked the box gets what they asked for.
 */
export function ConfirmArrivalResult({ result }: ConfirmArrivalResultProps) {
  const { t } = useTranslation();
  const { download } = useReceiveAttachmentDownload();
  const nothingWritten = result.receiveShipmentIds.length === 0;
  // A waybill receipt books one receipt per consignment; the breakdown says which
  // GRN belongs to which. One receipt needs no table — the badge says it all.
  const receipts = result.receipts ?? [];
  const showReceipts = receipts.length > 1;

  return (
    <div className="space-y-4">
      <Alert variant={nothingWritten ? 'info' : 'success'}>
        <CheckCircle2 />
        <AlertTitle>
          {nothingWritten
            ? t('receive.confirm.result.nothingReceived', 'No quantities were recorded')
            : t('receive.confirm.result.title', 'Receipt recorded')}
        </AlertTitle>
        <AlertDescription>
          {nothingWritten
            ? t(
                'receive.confirm.result.nothingReceivedDesc',
                'Every line was zero, so nothing was written. The shipment is still awaiting receipt.'
              )
            : t('receive.confirm.result.summary', '{{processed}} line(s) recorded at {{location}} on {{date}}', {
                processed: result.processedLines,
                location: result.receivedAtLocation,
                date: formatDisplayDate(result.receiveDate)
              })}
        </AlertDescription>
      </Alert>

      {result.goodsReceivedNoteNumbers.length > 0 && (
        <div className="space-y-1">
          {showReceipts ? (
            <ConfirmArrivalReceipts receipts={receipts} />
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">
                {t('grn.number.label', 'Goods Received Note Number')}
              </p>
              <div className="flex flex-wrap gap-2">
                {result.goodsReceivedNoteNumbers.map((number) => (
                  <Badge key={number} variant="outline" className="font-mono">
                    {number}
                  </Badge>
                ))}
              </div>
            </>
          )}
          {result.printGoodsReceivedNote && (
            <p className="text-xs text-muted-foreground">
              {t(
                'receive.confirm.result.printPending',
                'Printing the Goods Received Note is not available yet — note the number above.'
              )}
            </p>
          )}
        </div>
      )}

      {result.attachments?.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{t('newcmr.attachments.short', 'Attachments')}</p>
          <ItemGroup>
            {result.attachments.map((attachment) => (
              <AttachmentItem
                key={`${attachment.shipmentId}-${attachment.id}`}
                attachment={toAttachmentSummary(attachment)}
                onDownload={() => download(attachment)}
              />
            ))}
          </ItemGroup>
        </div>
      )}

      {result.skippedLines > 0 && (
        <p className="text-sm text-muted-foreground">
          {t('receive.confirm.result.skipped', '{{count}} line(s) skipped because every quantity was zero.', {
            count: result.skippedLines
          })}
        </p>
      )}

      {result.consignmentStatusChanges.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {t('receive.confirm.result.statusChanges', 'Consignment status changes')}
          </p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {result.consignmentStatusChanges.map((change) => (
              <li key={change.consignmentId}>
                {change.consignmentNumber}: {change.fromStatusCode} → {change.toStatusCode}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmArrivalWarnings warnings={result.warnings} />
    </div>
  );
}
